import { Injectable, Optional } from '@angular/core';
import { AngularFireAnalytics } from '@angular/fire/compat/analytics';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Organisations, CheckIns, APPLICATION_LOGIN_PROCESS_COMPLETE } from '../actions';
import { withLatestFrom, switchMap, filter } from 'rxjs/operators';
import { BestRolePipe } from '../../shared/pipes';
import { Store } from '@ngrx/store';
import { org } from '../reducers';
import { auth } from '../auth/auth.selectors';
import { Analytics } from '@careapp-group/core';
import { Memos } from '../memos/memos.actions';
import { AnalyticsActions } from './analytics.actions';

/**
 * Listens for important redux events and logs to Analytics
 */

@Injectable()
export class AnalyticsEffects {
  constructor(
    private actions$: Actions,
    private store: Store<any>,
    @Optional() private analytics: AngularFireAnalytics
  ) {}

  /**
   * Sets the ROLE user property when the organisation changes.
   */
  onSetOrganisation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.SELECT_BY_ID),
      filter((action) => !!this.analytics),
      withLatestFrom(this.store.select(org.currentOrgRoles)),
      switchMap(async ([action, roles]) => {
        const role = new BestRolePipe().transform(roles);
        await this.analytics.setUserProperties({
          [Analytics.userRole]: role,
        });
        return AnalyticsActions.AnalyticsAction();
      })
    )
  );

  /// Sets the user type (internal|external) on login.
  ///
  /// Users who login with a careapp.com.au email address are segmented
  /// to the internal group. Any other email is an external user
  ///
  onLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(APPLICATION_LOGIN_PROCESS_COMPLETE),
      filter((action) => !!this.analytics),
      withLatestFrom(this.store.select(auth.getLoggedInUser)),
      switchMap(async ([action, user]) => {
        await this.analytics.logEvent('login');

        if (user) {
          const isInternal = user.email.includes('careapp.com.au');

          await this.analytics.setUserProperties({
            [Analytics.userType]: isInternal ? Analytics.internalUser : Analytics.externalUser,
          });
        }

        return AnalyticsActions.AnalyticsAction();
      })
    )
  );

  onActivityLibraryDrive$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AnalyticsActions.ActivityLibraryDriveOpen),
        filter((action) => !!this.analytics),
        switchMap(async () => {
          await this.analytics.logEvent(Analytics.activityLibraryGDrive);
        })
      ),
    {
      dispatch: false,
    }
  );

  onHeart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.RESPONSE_SUCCESS_ADD_HEART),
      filter((action) => !!this.analytics),
      switchMap(async () => {
        await this.analytics.logEvent(Analytics.addHeart);
        return AnalyticsActions.AnalyticsAction();
      })
    )
  );

  onComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.RESPONSE_SUCCESS_ADD_COMMENT),
      filter((action) => !!this.analytics),
      switchMap(async () => {
        await this.analytics.logEvent(Analytics.addComment);
        return AnalyticsActions.AnalyticsAction();
      })
    )
  );

  onPostMemo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.RESPONSE_SUCCESS_CREATE),
      filter((action) => !!this.analytics),
      switchMap(async () => {
        await this.analytics.logEvent(Analytics.createMemo);
        return AnalyticsActions.AnalyticsAction();
      })
    )
  );

  onCheckIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckIns.ResponseSuccessCreateAction),
      filter((action) => !!this.analytics),
      switchMap(async () => {
        await this.analytics.logEvent(Analytics.checkedIn);
        return AnalyticsActions.AnalyticsAction();
      })
    )
  );
}
